import { useQuery, keepPreviousData } from '@tanstack/react-query'
import moment from 'moment'
import { axiosInstance } from '@/modules/shared/lib/axios'
import { AlertModel } from '@/app/models/alert/alertModel'

type AlertPreview = {
  items: Array<AlertModel>
  total: number
}

/**
 * The first `take` alerts of the company-wide [-1 month, +1 month] window plus the
 * total the window matched. Use this instead of `alert/alerts` wherever only a short
 * list and a "+N" counter are rendered — that endpoint returns every alert (241 on a
 * ~1500-driver company) and the count was being derived from the array length.
 */
export function useQueryAlertPreview(companyId: string, take = 8) {
  return useQuery<AlertPreview>({
    queryKey: ['alert-preview', companyId, take],
    queryFn: async () => {
      const res = await axiosInstance.get(`/api/${companyId}/alert/preview`, {
        params: { take },
      })
      const data = res.data?.data ?? res.data ?? {}
      const items = Array.isArray(data.items) ? data.items : []
      return {
        total: typeof data.total === 'number' ? data.total : items.length,
        items: items.map((alert: any, index: number) => ({
          type: alert.type,
          eventTime: moment(alert.eventTime),
          remindTime: alert.remindTime ? moment(alert.remindTime) : null,
          object: alert.object || '-',
          text: alert.text || alert.description || '',
          driverId: alert.driverId ? String(alert.driverId) : '',
          vehicleId: alert.vehicleId ? String(alert.vehicleId) : '',
          alertId: alert.id ? String(alert.id) : alert.alertId || '0',
          notRemindAnymore: alert.notRemindAnymore || false,
          index,
        })),
      }
    },
    enabled: !!companyId,
    staleTime: 30000,
    placeholderData: keepPreviousData,
  })
}
